import { BarcodeFormat, DecodeHintType } from "@zxing/library";

/** Только QR и Data Matrix (код маркировки), без EAN/CODE128. */
export function createMarkingZxingHints() {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.QR_CODE,
  ]);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return hints;
}

export const MARKING_ZXING_HINTS = createMarkingZxingHints();

/** GS1 Group Separator (FNC1) — ASCII 0x1D. */
export const GS1_SEPARATOR = String.fromCharCode(29);

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const LINEAR_BARCODE_RE = /^\d{8,14}$/;

/** Товарный штрихкод EAN-8/EAN-13 и т.п. (только цифры, 8–14 символов). */
export function isPlainLinearBarcode(raw) {
  const s = String(raw || "").trim();
  return LINEAR_BARCODE_RE.test(s);
}

function gs1CheckDigitOk(digits) {
  const body = digits.slice(0, -1);
  let total = 0;
  for (let i = 0; i < body.length; i += 1) {
    const d = Number(body[body.length - 1 - i]);
    total += i % 2 === 0 ? d * 3 : d;
  }
  return (10 - (total % 10)) % 10 === Number(digits[digits.length - 1]);
}

/**
 * SSCC кода коробки (КИТУ, GS1-128 «(00)» + 18 цифр) — 18 цифр или null.
 * Сканер может прислать префикс ]C1, FNC1, скобки AI; контрольная цифра обязательна.
 */
export function extractSscc(raw) {
  let s = String(raw || "").replace(/[\x00-\x1f\x7f\s()]+/g, "");
  if (s.startsWith("]C1")) s = s.slice(3);
  if (!/^\d+$/.test(s)) return null;
  if (s.length === 20 && s.startsWith("00")) s = s.slice(2);
  if (s.length !== 18) return null;
  return gs1CheckDigitOk(s) ? s : null;
}

// Barcode стикера WB — алфавит base64: кроме букв/цифр бывают «+» и «/» (*DcarK7+R).
const WB_STICKER_BARCODE_RE = /^[!*][A-Za-z0-9+/]+$/;
const WB_STICKER_BODY_RE = /^[A-Za-z0-9+/]{4,31}$/;
const WB_STICKER_TOKEN_RE = /[!*][A-Za-z0-9+/]{3,31}/;
const GS1_KM_START_RE = /^01\d{14}21/;
const CYRILLIC_CHARS_RE = /[а-яА-ЯёЁ]/;

/** ЙЦУКЕН → QWERTY: HID-сканер при русской раскладке OS даёт кириллицу вместо латиницы. */
const RU_LAYOUT_TO_EN = {
  й: "q",
  ц: "w",
  у: "e",
  к: "r",
  е: "t",
  н: "y",
  г: "u",
  ш: "i",
  щ: "o",
  з: "p",
  х: "[",
  ъ: "]",
  ф: "a",
  ы: "s",
  в: "d",
  а: "f",
  п: "g",
  р: "h",
  о: "j",
  л: "k",
  д: "l",
  ж: ";",
  э: "'",
  я: "z",
  ч: "x",
  с: "c",
  м: "v",
  и: "b",
  т: "n",
  ь: "m",
  б: ",",
  ю: ".",
  ё: "`",
  Й: "Q",
  Ц: "W",
  У: "E",
  К: "R",
  Е: "T",
  Н: "Y",
  Г: "U",
  Ш: "I",
  Щ: "O",
  З: "P",
  Х: "{",
  Ъ: "}",
  Ф: "A",
  Ы: "S",
  В: "D",
  А: "F",
  П: "G",
  Р: "H",
  О: "J",
  Л: "K",
  Д: "L",
  Ж: ":",
  Э: '"',
  Я: "Z",
  Ч: "X",
  С: "C",
  М: "V",
  И: "B",
  Т: "N",
  Ь: "M",
  Б: "<",
  Ю: ">",
  Ё: "~",
};

/** Переложить символы русской раскладки в латинские (как если бы была EN). */
export function remapRuLayoutToEn(raw) {
  const s = String(raw || "");
  if (!CYRILLIC_CHARS_RE.test(s)) return s;
  let out = "";
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    out += Object.prototype.hasOwnProperty.call(RU_LAYOUT_TO_EN, ch)
      ? RU_LAYOUT_TO_EN[ch]
      : ch;
  }
  return out;
}

function stripControlChars(s) {
  let out = "";
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code > 31 && code !== 127) out += s[i];
  }
  return out;
}

/** Убрать мусор сканера и извлечь токен QR стикера WB. */
export function normalizeWbStickerBarcode(raw) {
  let s = String(raw || "").trim();
  s = stripControlChars(s).trim();
  if (CYRILLIC_CHARS_RE.test(s)) {
    // «/» при русской раскладке приходит как «.» (та же клавиша).
    s = remapRuLayoutToEn(s).replace(/\./g, "/");
  }
  for (const prefix of ["]C1", "]Q3", "]d2"]) {
    if (s.startsWith(prefix)) {
      s = s.slice(prefix.length).trim();
      break;
    }
  }
  // КМ Честного знака: в серийнике бывают «*» и «!» — не выдёргивать из него «токен стикера».
  if (GS1_KM_START_RE.test(s)) return s;
  if (WB_STICKER_BARCODE_RE.test(s)) return s;
  const m = s.match(WB_STICKER_TOKEN_RE);
  if (m) return m[0];
  return s;
}

/** QR стикера сборочного задания WB (FBS): !uKEtQZVx, *C95DVNYH и т.п. */
export function isWbStickerBarcode(raw) {
  const s = normalizeWbStickerBarcode(raw);
  if (!s || s.length > 32 || s.length < 4) return false;
  if (isPlainLinearBarcode(s)) return false;
  // 20 цифр кода коробки иначе проходят под WB_STICKER_BODY_RE.
  if (extractSscc(s)) return false;
  if (s.startsWith("01") && s.length >= 16) return false;
  if (WB_STICKER_BARCODE_RE.test(s)) return true;
  return WB_STICKER_BODY_RE.test(s);
}

/** Сообщение об ошибке или null, если скан допустим для маркировки. */
export function getMarkingScanRejectMessage(raw) {
  if (isPlainLinearBarcode(raw)) {
    return "Товарный штрихкод (EAN) не принимается. Сканируйте QR или код маркировки (Data Matrix).";
  }
  if (isWbStickerBarcode(raw)) {
    return null;
  }
  return null;
}

export function unwrapCrptPayload(data) {
  if (!data || typeof data !== "object") return data;
  if (data.checkResponse != null && typeof data.checkResponse === "object") {
    return data.checkResponse;
  }
  return data;
}

export function crptExtractGtin(data) {
  const d = unwrapCrptPayload(data);
  if (d?.gtin != null && String(d.gtin).trim()) return String(d.gtin).trim();
  const items = d?.items;
  if (Array.isArray(items) && items.length > 0) {
    const ean = items[0]?.ean13;
    if (ean != null && String(ean).trim()) return String(ean).trim();
  }
  const cr = d?.codeResolveData;
  if (!cr || typeof cr !== "object") return null;
  const g = cr.gtin ?? cr.ais?.gtin;
  return g != null && String(g).trim() ? String(g).trim() : null;
}

export function crptParseFailed(crptData) {
  const d = unwrapCrptPayload(crptData);
  if (d?.type === "error" && !crptExtractGtin(crptData)) return true;
  const cr = d?.codeResolveData;
  if (!cr || typeof cr !== "object") return false;
  // found=false при valid=true (not_found_dm) — код разобран, товар определяется по GTIN.
  if (cr.valid === false) return true;
  const msg = String(cr.message || "");
  return msg.length > 0 && /cannot parse|not supported|aigroupnotsupported/i.test(msg);
}

/** Ответ ЧЗ пригоден для определения товара (как в официальном приложении). */
export function crptResponseUsable(crptData) {
  if (crptParseFailed(crptData)) return false;
  const d = unwrapCrptPayload(crptData);
  if (d?.type && d.type !== "error" && crptExtractGtin(crptData)) return true;
  const cr = d?.codeResolveData;
  if (!cr || typeof cr !== "object") return false;
  if (cr.valid === true && crptExtractGtin(crptData)) return true;
  return cr.found === true && cr.valid === true;
}

/** Полный успешный ответ ЧЗ (checkResult + GTIN). */
export function crptResponseBest(crptData) {
  if (crptParseFailed(crptData)) return false;
  const d = unwrapCrptPayload(crptData);
  if (!d || typeof d !== "object") return false;
  if (d.checkResult === true && crptExtractGtin(crptData)) return true;
  return crptResponseUsable(crptData);
}

/** Ранг ответа ЧЗ (выше = лучше) для выбора среди вариантов строки. */
export function crptResponseRank(crptData) {
  if (crptParseFailed(crptData)) return 0;
  const d = unwrapCrptPayload(crptData);
  if (!d || typeof d !== "object") return 0;
  let rank = 1;
  if (crptExtractGtin(crptData)) rank = 2;
  if (d.type === "item" || d.type === "group" || d.type === "aggregation") rank = 3;
  if (d.checkResult === true) rank = 3;
  if (d.petfoodData || d.catalogData) rank = 4;
  return rank;
}

/**
 * Вставить GS-разделители в КМ, если сканер склеил AI 21/91/92 без FNC1.
 * Нужно для полного ответа ЧЗ и для WB meta/sgtin.
 */
export function normalizeMarkingCodeGs1(raw) {
  // Ведущий GS (FNC1 начала кода) или GS в конце от сканера — снимаем, иначе разделитель
  // перед AI 91/93 не вставится и WB ответит «Неверная структура маркировки».
  const gsEdgesRe = new RegExp(`^${escapeRegExp(GS1_SEPARATOR)}+|${escapeRegExp(GS1_SEPARATOR)}+$`, "g");
  const s = String(raw || "").trim().replace(gsEdgesRe, "");
  if (!s || s.includes(GS1_SEPARATOR)) return s;
  if (!/^01\d{14}21/.test(s)) return s;
  let m = s.match(/^(01\d{14}21.+?)(91.{4})(92.+)$/);
  if (m) return m[1] + GS1_SEPARATOR + m[2] + GS1_SEPARATOR + m[3];
  m = s.match(/^(01\d{14}21.+?)(91.{4})$/);
  if (m) return m[1] + GS1_SEPARATOR + m[2];
  // AI 93 — короткий криптохвост (молоко, вода, пиво): всегда 4 символа в конце КМ.
  m = s.match(/^(01\d{14}21.{4,})(93.{4})$/);
  if (m) return m[1] + GS1_SEPARATOR + m[2];
  return s + GS1_SEPARATOR;
}

/** Код для applyMarking / WB: полный КМ с GS из сырого скана. */
export function markingCodeForApply(rawScan) {
  const sscc = extractSscc(rawScan);
  if (sscc) return `00${sscc}`;
  return normalizeMarkingCodeGs1(rawScan);
}

/**
 * Варианты строки для mobile/check.
 * 1) с GS (полный ответ ЧЗ), 2) сырой скан, 3) укороченный до AI 91.
 */
export function markingCodeCrptCandidates(raw) {
  const s = String(raw || "").trim();
  if (!s) return [];
  const out = [];
  const add = (c) => {
    const t = String(c || "").trim();
    if (t && !out.includes(t)) out.push(t);
  };
  const withGs = normalizeMarkingCodeGs1(s);
  add(withGs);
  add(s);
  const m = s.match(/^(01\d{14}21.+?)91/);
  if (m?.[1] && m[1].length >= 20) add(m[1]);
  const gsEsc = escapeRegExp(GS1_SEPARATOR);
  const mGs = withGs.match(new RegExp(`^(01\\d{14}21.+?)${gsEsc}91`));
  if (mGs?.[1] && mGs[1].length >= 20) add(mGs[1]);
  return out;
}

const PACKAGING_INDICATORS = "12345678";

function normalizeGtinDigits(digits) {
  if (!digits || digits.length < 8) return null;
  let s = digits.replace(/^0+/, "") || digits;
  if (s.length === 14 && PACKAGING_INDICATORS.includes(s[0])) {
    s = s.slice(1);
  }
  return s;
}

export function gtinMatchKey(gtin) {
  if (!gtin) return null;
  const digits = String(gtin).replace(/\D/g, "");
  return normalizeGtinDigits(digits);
}

/** Все варианты GTIN для сравнения (13↔14, ITF-14 с индикатором упаковки). */
export function gtinMatchKeys(gtin) {
  if (!gtin) return new Set();
  // Несколько GTIN в одном поле склада (новый + старый штрихкод): перенос строки, запятая и т.п.
  const parts = String(gtin).split(/[\s,;|/]+/).filter(Boolean);
  if (parts.length > 1) {
    const all = new Set();
    for (const part of parts) {
      for (const k of gtinMatchKeys(part)) all.add(k);
    }
    return all;
  }
  const digits = String(gtin).replace(/\D/g, "");
  if (digits.length < 8) return new Set();
  const keys = new Set();
  const base = normalizeGtinDigits(digits);
  if (base) keys.add(base);
  const raw = digits.replace(/^0+/, "") || digits;
  if (raw) keys.add(raw);
  if (base && base.length === 13) {
    keys.add(`0${base}`);
    for (const d of PACKAGING_INDICATORS) keys.add(`${d}${base}`);
  }
  if (base && base.length === 14 && base[0] === "0") keys.add(base.slice(1));
  return keys;
}

export function gtinMatches(a, b) {
  const ka = gtinMatchKeys(a);
  const kb = gtinMatchKeys(b);
  for (const x of ka) {
    if (kb.has(x)) return true;
  }
  return false;
}

/**
 * GTIN из сырого Data Matrix (AI 01, 14 цифр), без mobile.api.crpt.ru.
 * Нужен, когда ЧЗ не разбирает КМ (например серийник начинается с «5» → AiGroupNotSupportedException).
 */
export function extractGtinFromGs1(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  const gsEsc = escapeRegExp(GS1_SEPARATOR);
  const m = s.match(new RegExp(`(?:${gsEsc})?01(\\d{14})`));
  if (m) {
    return normalizeGtinDigits(m[1]);
  }
  const mLead = s.match(/^01(\d{14})/);
  if (mLead) {
    return normalizeGtinDigits(mLead[1]);
  }
  return null;
}

export function crptExtractPackType(crptData) {
  const d = unwrapCrptPayload(crptData);
  if (!d || typeof d !== "object") return null;
  if (d.pack_type != null && String(d.pack_type).trim()) {
    const pt = String(d.pack_type).trim().toUpperCase();
    if (/^LEVEL[1-5]$/.test(pt)) return "GROUP";
    return pt;
  }
  if (d.type === "group" || d.type === "aggregation") return "GROUP";
  for (const key of ["petfoodData", "catalogData"]) {
    const block = d[key];
    const pt = block?.productProperty?.packType;
    if (pt != null && String(pt).trim()) return String(pt).trim().toUpperCase();
  }
  return null;
}

/** GTIN-14 из AI (01) в КМ — как на упаковке, без нормализации к штуке. */
export function extractGtin14FromGs1(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  const gsEsc = escapeRegExp(GS1_SEPARATOR);
  const m = s.match(new RegExp(`(?:${gsEsc})?01(\\d{14})`));
  if (m) return m[1];
  const mLead = s.match(/^01(\d{14})/);
  if (mLead) return mLead[1];
  return null;
}

/** Штучный КМ (потребительская упаковка) vs групповая/транспортная. */
export function isConsumerUnitMarkingCode(raw) {
  const gtin14 = extractGtin14FromGs1(raw);
  if (!gtin14) return true;
  if (PACKAGING_INDICATORS.includes(gtin14[0])) return false;
  return gtin14[0] === "0" || gtin14[0] === "9";
}

/** Штучный КМ vs упаковка для блочного товара (GTIN единицы в AI 01 с ведущим 0). */
export function isUnitScanForBlockProduct(raw, expectedGtin) {
  const gtin14 = extractGtin14FromGs1(raw);
  if (!gtin14) return false;
  if (PACKAGING_INDICATORS.includes(gtin14[0])) return false;
  const scanKey = normalizeGtinDigits(gtin14);
  if (!scanKey || !expectedGtin) return isConsumerUnitMarkingCode(raw);
  const expKeys = gtinMatchKeys(expectedGtin);
  return expKeys.has(scanKey);
}

/** GTIN: сначала ответ ЧЗ, иначе локальный разбор GS1 из скана. */
export function resolveGtinFromScan(crptData, rawScan) {
  const fromCrpt = crptExtractGtin(crptData);
  if (fromCrpt) return fromCrpt;
  return extractGtinFromGs1(rawScan);
}

/**
 * GTIN для applyMarking: приоритет AI (01) из отсканированного КМ (упаковка),
 * иначе ответ ЧЗ / GS1.
 */
export function resolveGtinForApply(crptData, rawScan) {
  // Код коробки: GTIN штук внутри — только из ответа ЧЗ (в цифрах SSCC «01» встречается случайно).
  if (extractSscc(rawScan)) return crptExtractGtin(crptData);
  const fromKm = extractGtin14FromGs1(rawScan);
  if (fromKm) return fromKm;
  return resolveGtinFromScan(crptData, rawScan);
}

const CIS_BODY_RE = /01\d{14}21/i;

function collectCisStringsFromObj(obj, out, depth = 0) {
  if (!obj || depth > 20) return;
  if (typeof obj === "string") {
    const s = obj.trim();
    if (s && CIS_BODY_RE.test(s)) {
      const norm = normalizeMarkingCodeGs1(s);
      if (norm && !out.includes(norm)) out.push(norm);
    }
    return;
  }
  if (Array.isArray(obj)) {
    for (const item of obj) collectCisStringsFromObj(item, out, depth + 1);
    return;
  }
  if (typeof obj === "object") {
    for (const [key, val] of Object.entries(obj)) {
      const k = String(key || "").toLowerCase();
      if (
        ["cis", "sgtin", "code", "markingcode", "identificationcode", "kiz", "km", "scannedcode"].includes(k) &&
        typeof val === "string"
      ) {
        const s = val.trim();
        if (s && CIS_BODY_RE.test(s)) {
          const norm = normalizeMarkingCodeGs1(s);
          if (norm && !out.includes(norm)) out.push(norm);
        }
      }
      collectCisStringsFromObj(val, out, depth + 1);
    }
  }
}

function markingCodesEquivalentLocal(a, b) {
  const x = String(a || "").trim();
  const y = String(b || "").trim();
  if (!x || !y) return false;
  if (x === y) return true;
  return false;
}

/** Штучные КИЗ из ответа ЧЗ при скане упаковки (блок). */
export function extractUnitMarkingCodesFromCrpt(crptData, packagingCode, expectedGtin) {
  const packNorm = normalizeMarkingCodeGs1(String(packagingCode || "").trim());
  const d = unwrapCrptPayload(crptData);
  if (Array.isArray(d?.items)) {
    const fromItems = [];
    for (const it of d.items) {
      for (const c of it.cis || []) {
        const code = normalizeMarkingCodeGs1(String(c || "").trim());
        if (!code || code === packNorm) continue;
        if (expectedGtin && !isUnitScanForBlockProduct(code, expectedGtin) && !isConsumerUnitMarkingCode(code)) {
          continue;
        }
        if (!expectedGtin && !isConsumerUnitMarkingCode(code)) continue;
        if (fromItems.some((u) => markingCodesEquivalentLocal(u, code))) continue;
        fromItems.push(code);
      }
    }
    if (fromItems.length) return fromItems;
  }
  const found = [];
  collectCisStringsFromObj(d, found);
  const units = [];
  for (const code of found) {
    const c = String(code || "").trim();
    if (!c || c === packNorm) continue;
    if (expectedGtin) {
      const gtin14 = extractGtin14FromGs1(c);
      if (gtin14 && !isUnitScanForBlockProduct(c, expectedGtin) && !isConsumerUnitMarkingCode(c)) {
        continue;
      }
    } else if (!isConsumerUnitMarkingCode(c)) {
      continue;
    }
    if (units.some((u) => markingCodesEquivalentLocal(u, c))) continue;
    units.push(c);
  }
  return units;
}

export function focusInputNoScroll(inputEl) {
  if (!inputEl || typeof inputEl.focus !== "function") return;
  try {
    inputEl.focus({ preventScroll: true });
  } catch {
    inputEl.focus();
  }
}

/* --- Срок годности из ответа Честного знака --- */

/**
 * Ключи, под которыми ЧЗ отдаёт дату истечения срока годности.
 * Зеркало _EXPIRATION_KEYS в server/app/utils/marking_scan.py — правим оба места вместе.
 */
const EXPIRATION_KEYS = new Set(["expirationDate", "expireDate", "expirationStorageDate"]);

/** Календарная дата как UTC-полночь — чтобы разница в днях считалась без сдвигов зоны. */
function utcDay(y, m, d) {
  const t = Date.UTC(y, m - 1, d);
  return Number.isNaN(t) ? null : new Date(t);
}

/**
 * Дата из ЧЗ: ISO (`2027-08-27T00:00:00.000Z`), Unix-секунды или Unix-миллисекунды.
 * У ISO берём дату как есть, без перевода зоны. Unix разворачиваем в UTC: если
 * производитель подал полночь по Москве, дата съедет на день назад — для «годен до»
 * занижение безопасно, завышение пропустило бы просрочку.
 */
export function parseCrptDate(value) {
  if (value == null || typeof value === "boolean") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  let num = null;
  if (typeof value === "number") {
    num = value;
  } else {
    const s = String(value).trim();
    if (!s) return null;
    let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return utcDay(Number(m[1]), Number(m[2]), Number(m[3]));
    m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s);
    if (m) return utcDay(Number(m[3]), Number(m[2]), Number(m[1]));
    if (!/^\d{9,14}$/.test(s)) return null;
    num = Number(s);
  }
  if (!Number.isFinite(num) || num <= 0) return null;
  const ms = num > 1e11 ? num : num * 1000;
  const dt = new Date(ms);
  if (Number.isNaN(dt.getTime())) return null;
  return utcDay(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

function collectExpirationDates(obj, out, depth = 0) {
  if (depth > 8 || obj == null) return;
  if (Array.isArray(obj)) {
    for (const v of obj.slice(0, 500)) collectExpirationDates(v, out, depth + 1);
    return;
  }
  if (typeof obj !== "object") return;
  for (const [k, v] of Object.entries(obj)) {
    if (EXPIRATION_KEYS.has(k)) {
      const d = parseCrptDate(v);
      if (d) out.push(d);
    } else {
      collectExpirationDates(v, out, depth + 1);
    }
  }
}

/**
 * Срок годности из ответа ЧЗ — самый ранний из найденных.
 * Обходим payload целиком: дата лежит в разных местах в зависимости от канала и упаковки
 * (cis_info по штуке, petfoodData в mobile/check, items[].good у коробки и блока).
 * Для коробки минимум — единственно верный выбор: партия годна по худшей своей единице.
 */
export function extractExpirationDate(crptData) {
  const found = [];
  collectExpirationDates(unwrapCrptPayload(crptData), found);
  if (found.length === 0) collectExpirationDates(crptData, found);
  if (found.length === 0) return null;
  return found.reduce((a, b) => (a.getTime() <= b.getTime() ? a : b));
}

/** Сколько дней товар ещё годен. 0 — последний день, отрицательное — просрочен. */
export function expirationDaysLeft(expiration, today = new Date()) {
  if (!expiration) return null;
  const base = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((expiration.getTime() - base) / 86400000);
}

/** Формат WB meta/expiration и подписи в интерфейсе — DD.MM.YYYY. */
export function formatExpirationRu(expiration) {
  if (!expiration) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(expiration.getUTCDate())}.${p(expiration.getUTCMonth() + 1)}.${expiration.getUTCFullYear()}`;
}
